import { configureStore } from '@reduxjs/toolkit'
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux'
import { authReducer } from './slice'
import { posthogMiddleware } from './middleware/posthog'
import type { AuthHandlers } from '../types.d'
import type { MutableRefObject } from 'react'

export const createAuthStore = (
  handlers: AuthHandlers,
  authCounterRef: MutableRefObject<number>,
) => {
  return configureStore({
    reducer: {
      auth: authReducer,
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: { ...handlers, authCounterRef },
        },
      }).concat(posthogMiddleware),
    devTools: process.env.NODE_ENV !== 'production',
  })
}

export type AuthStore = ReturnType<typeof createAuthStore>
export type RootState = ReturnType<AuthStore['getState']>
export type AppDispatch = AuthStore['dispatch']

// Modern Redux Toolkit typed hooks
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

// Convenience selectors for common auth state
export const selectAuthState = (state: RootState) => state.auth
export const selectAuthStep = (state: RootState) => state.auth.step
export const selectAuthError = (state: RootState) => state.auth.error
export const selectAuthLoading = (state: RootState) => state.auth.loading
export const selectSigninData = (state: RootState) => state.auth.signin
export const selectSignupData = (state: RootState) => state.auth.signup
export const selectAuthConfig = (state: RootState) => state.auth.config
export const selectAuthMethod = (state: RootState) =>
  state.auth.signin?.authMethod?.type
